import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Search as SearchIcon } from "@mui/icons-material";
import { InputAdornment, TextField, Switch } from "@mui/material";
import { Image, Spinner } from "react-bootstrap";
import moment from "moment";
import apiClient from "../../api/apiClient";
import { upload_cms_photo } from "../../reduxData/cms/cmsAction";
import { useDispatch } from "react-redux";

interface Item {
  _id: string;
  item_name: string;
  slug: string;
  thumbnails?: string[];
  bid_price?: number;
  starting_bid?: number;
  end_date?: string;
  status?: string;
}

interface Collection {
  _id: string;
  name: string;
  slug: string;
  description: string;
  coverImage: string | null;
  items: Item[];
  isActive: boolean;
  createdAt: string;
}

type View = "list" | "edit";

const CollectionsManager: React.FC<{ user?: any }> = ({ user }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState<View>("list");
  const [collections, setCollections] = useState<Collection[]>([]);
  const [total, setTotal] = useState(0);
  const [listSearch, setListSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);

  const [editing, setEditing] = useState<Collection | null>(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formCover, setFormCover] = useState("");
  const [formItems, setFormItems] = useState<Item[]>([]);
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [formItemsLoading, setFormItemsLoading] = useState(false);
  const coverRef = useRef<HTMLInputElement>(null);

  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemsLoading, setItemsLoading] = useState(false);
  const itemSearchTimeout = useRef<any>(null);

  const isPermission =
    (user?.role === "staff" && user?.permission?.p_staff > 1) ||
    user?.role !== "staff";

  const fetchCollections = async (search = "") => {
    setListLoading(true);
    try {
      const res = await apiClient.get(
        `/collections?search=${encodeURIComponent(search)}&limit=50`,
      );
      if (res.data.status) {
        setCollections(res.data.data);
        setTotal(res.data.total);
      }
    } catch {
      toast.error("Failed to load collections");
    } finally {
      setListLoading(false);
    }
  };

  const fetchAvailableItems = async (search = "") => {
    setItemsLoading(true);
    try {
      const res = await apiClient.get(
        `/collections/available-items?search=${encodeURIComponent(search)}&limit=24`,
      );
      if (res.data.status) setAvailableItems(res.data.data);
    } catch {
      toast.error("Failed to load items");
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (view === "edit") fetchAvailableItems();
  }, [view]);

  useEffect(() => {
    clearTimeout(itemSearchTimeout.current);
    itemSearchTimeout.current = setTimeout(
      () => fetchAvailableItems(itemSearch),
      400,
    );
    return () => clearTimeout(itemSearchTimeout.current);
  }, [itemSearch]);

  const openNew = () => {
    setEditing(null);
    setFormName("");
    setFormDesc("");
    setFormCover("");
    setFormItems([]);
    setFormActive(true);
    setItemSearch("");
    setView("edit");
  };

  const openEdit = async (col: Collection) => {
    setEditing(col);
    setFormName(col.name);
    setFormDesc(col.description);
    setFormCover(col.coverImage || "");
    setFormActive(col.isActive);
    setItemSearch("");
    setFormItems([]);
    setView("edit");

    setFormItemsLoading(true);
    try {
      const res = await apiClient.get(`/collections/${col._id}`);
      if (res.data.status) {
        setFormItems(res.data.data.items || []);
      }
    } catch {
      toast.error("Failed to load collection items");
    } finally {
      setFormItemsLoading(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const result = await upload_cms_photo(
        [file],
        localStorage.getItem("token"),
        dispatch,
      );
      if (result?.data?.photos?.[0]) setFormCover(result.data.photos[0]);
      else toast.error("Upload failed");
    } catch {
      toast.error("Upload failed");
    } finally {
      setCoverUploading(false);
      if (coverRef.current) coverRef.current.value = "";
    }
  };

  const toggleItem = (item: Item) => {
    setFormItems((prev) =>
      prev.some((i) => i._id === item._id)
        ? prev.filter((i) => i._id !== item._id)
        : [...prev, item],
    );
  };

  const removeItem = (id: string) =>
    setFormItems((prev) => prev.filter((i) => i._id !== id));

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        description: formDesc.trim(),
        coverImage: formCover || null,
        items: formItems.map((i) => i._id),
        isActive: formActive,
      };
      const res = editing
        ? await apiClient.put(`/collections/${editing._id}`, payload)
        : await apiClient.post("/collections", payload);

      if (res.data.status) {
        toast.success(res.data.message);
        setView("list");
        fetchCollections(listSearch);
      }
    } catch {
      toast.error("Failed to save collection");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const res = await apiClient.delete(`/collections/${id}`);
      if (res.data.status) {
        toast.success("Collection deleted");
        fetchCollections(listSearch);
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (view === "edit") {
    const selectedIds = new Set(formItems.map((i) => i._id));

    return (
      <div
        className="container-fluid"
        style={{ maxWidth: 1100, paddingTop: 8 }}
      >
        <div className="d-flex align-items-center gap-3 mb-4">
          <button
            className="btn btn-link p-0 text-dark fw-bold"
            onClick={() => setView("list")}
          >
            ← Back
          </button>
          <h3 className="fw-bold mb-0">
            {editing ? "Edit Collection" : "New Collection"}
          </h3>
        </div>

        <div className="row g-4">
          <div className="col-md-5">
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-3">
              <h6 className="fw-bold mb-3">Details</h6>

              <div className="mb-3">
                <label className="form-label fw-600 small">Name *</label>
                <input
                  className="form-control"
                  placeholder="e.g. Nintendo Summer Collection"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  disabled={!isPermission}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-600 small">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Optional short description..."
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  disabled={!isPermission}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-600 small">Cover Image</label>
                {formCover && !coverUploading && (
                  <div
                    className="mb-2 position-relative"
                    style={{ maxWidth: 200 }}
                  >
                    <Image src={formCover} fluid rounded />
                    <button
                      className="btn btn-sm btn-danger position-absolute"
                      style={{
                        top: 4,
                        right: 4,
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        padding: 0,
                      }}
                      onClick={() => setFormCover("")}
                    >
                      ×
                    </button>
                  </div>
                )}
                {coverUploading && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Spinner size="sm" />
                    <span className="small">Uploading...</span>
                  </div>
                )}
                <input
                  type="file"
                  ref={coverRef}
                  className="d-none"
                  accept="image/*"
                  onChange={handleCoverUpload}
                />
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => coverRef.current?.click()}
                  disabled={coverUploading || !isPermission}
                >
                  {formCover ? "Change Image" : "Upload Image"}
                </button>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Switch
                  checked={formActive}
                  onChange={(e) => setFormActive(e.target.checked)}
                  size="small"
                />
                <span className="small fw-600">
                  {formActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {formItemsLoading && (
              <div className="card border-0 shadow-sm rounded-4 p-4 text-center">
                <Spinner size="sm" />
                <span className="small text-muted mt-2">Loading items...</span>
              </div>
            )}

            {!formItemsLoading && formItems.length > 0 && (
              <div className="card border-0 shadow-sm rounded-4 p-4">
                <h6 className="fw-bold mb-3">
                  Selected Items ({formItems.length})
                </h6>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {formItems.map((item) => (
                    <div
                      key={item._id}
                      className="d-flex align-items-center gap-2 mb-2 p-2 border rounded-3"
                    >
                      {item.thumbnails?.[0] && (
                        <img
                          src={item.thumbnails[0]}
                          alt={item.item_name}
                          style={{
                            width: 36,
                            height: 36,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                        />
                      )}
                      <span className="small fw-600 flex-grow-1 text-truncate">
                        {item.item_name}
                      </span>
                      <button
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => removeItem(item._id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="col-md-7">
            <div className="card border-0 shadow-sm rounded-4 p-4">
              <h6 className="fw-bold mb-3">Add Auction Items</h6>

              <TextField
                fullWidth
                size="small"
                placeholder="Search auction items..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="mb-3"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {itemsLoading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" />
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 10,
                    maxHeight: 500,
                    overflowY: "auto",
                  }}
                >
                  {availableItems.map((item) => {
                    const selected = selectedIds.has(item._id);
                    return (
                      <div
                        key={item._id}
                        onClick={() => toggleItem(item)}
                        style={{
                          borderRadius: 10,
                          overflow: "hidden",
                          cursor: "pointer",
                          border: selected
                            ? "2.5px solid #FFD600"
                            : "2px solid #eee",
                          position: "relative",
                          aspectRatio: "1",
                          background: "#f5f5f5",
                          transition: "border-color 0.1s",
                        }}
                      >
                        {item.thumbnails?.[0] ? (
                          <img
                            src={item.thumbnails[0]}
                            alt={item.item_name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              background: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.65rem",
                                color: "#999",
                                textAlign: "center",
                                padding: 4,
                              }}
                            >
                              No image
                            </span>
                          </div>
                        )}
                        {selected && (
                          <div
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              background: "#FFD600",
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 900,
                              fontSize: 12,
                            }}
                          >
                            ✓
                          </div>
                        )}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(0,0,0,0.7)",
                            padding: "4px 6px",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: "0.62rem",
                              color: "#fff",
                              fontWeight: 700,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.item_name}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {availableItems.length === 0 && !itemsLoading && (
                    <div
                      className="text-muted small text-center py-3"
                      style={{ gridColumn: "1/-1" }}
                    >
                      No items found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4 pb-4">
          <button
            className="btn btn-light border"
            onClick={() => setView("list")}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn common-button"
            onClick={handleSave}
            disabled={saving || !isPermission}
          >
            {saving ? "Saving..." : "Save Collection"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ maxWidth: 960, paddingTop: 8 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0">
          Collections{" "}
          <span className="text-muted fs-5 fw-normal">({total})</span>
        </h3>
        <button
          className="btn common-button"
          onClick={openNew}
          disabled={!isPermission}
        >
          + New Collection
        </button>
      </div>

      <TextField
        fullWidth
        size="small"
        placeholder="Search collections..."
        value={listSearch}
        onChange={(e) => {
          setListSearch(e.target.value);
          fetchCollections(e.target.value);
        }}
        className="mb-4"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {listLoading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : collections.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover user-management-table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Name</th>
                <th>Items</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((col) => (
                <tr key={col._id}>
                  <td>
                    {col.coverImage ? (
                      <img
                        src={col.coverImage}
                        alt={col.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 6,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          background: "#eee",
                          borderRadius: 6,
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <div className="fw-600">{col.name}</div>
                    {col.description && (
                      <div className="text-muted small">
                        {col.description.slice(0, 60)}
                        {col.description.length > 60 ? "..." : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-light text-dark border">
                      {col.items?.length || 0} items
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${col.isActive ? "bg-success" : "bg-secondary"}`}
                    >
                      {col.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="small text-muted">
                    {moment(col.createdAt).format("MMM D, YYYY")}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 me-3 text-secondary fw-bold"
                      title="Copy collection URL"
                      onClick={() => {
                        const url = `${process.env.REACT_APP_FRONTEND_URL || "https://insertbid.com"}/collection/${col.slug}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Link copied!");
                      }}
                    >
                      Copy Link
                    </button>
                    <button
                      className="btn btn-sm btn-link p-0 me-3 text-primary fw-bold"
                      onClick={() => openEdit(col)}
                    >
                      Edit
                    </button>
                    {isPermission && (
                      <button
                        className="btn btn-sm btn-link p-0 text-danger fw-bold"
                        onClick={() => handleDelete(col._id, col.name)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <p className="fw-bold">No collections yet</p>
          <p className="small">Create your first collection to get started.</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ user: state.auth.user });
export default connect(mapStateToProps)(CollectionsManager);
